import React from "react";
import { Button, Input, Table } from "reactstrap";

import { formatDelta } from "../../helpers/partnerReports";

// Édition des blocs de chiffres (instantané figé à la génération). Blocs détaillés (évolution, répartitions, prix…) : lecture seule.
const KPI_LABELS = { clicks: "Clics envoyés", sales: "Ventes", revenue: "Chiffre d'affaires", conversion: "Taux de conversion (0–1)", basket: "Panier moyen", visitors: "Visiteurs uniques", views: "Vues des fiches", ctr: "Taux de clic (0–1)", identifiedSales: "Ventes identifiées", identifiedRevenue: "CA identifié", estimatedPairs: "Paires vendues (estimation)", estimatedRevenue: "CA estimé" };
const num = (v) => (v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v));
const small = { maxWidth: 120 };

function Kpis({ data, onChange }) {
    const rows = data.rows || [];
    const set = (i, patch) => onChange({ ...data, rows: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
    return (
        <Table size="sm" className="mb-0">
            <thead><tr><th>Indicateur</th><th>Valeur</th><th>Précédente</th><th>Évolution</th></tr></thead>
            <tbody>{rows.map((r, i) => r.key.startsWith("estimated") ? (
                <tr key={r.key}>
                    <td>{KPI_LABELS[r.key] || r.key}</td>
                    <td colSpan={3}>
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                            <span className="small text-muted">Basse</span><Input bsSize="sm" type="number" step="any" style={small} value={r.low ?? 0} onChange={(e) => { const low = num(e.target.value); set(i, { low, value: (low + (r.high ?? 0)) / 2 }); }} />
                            <span className="small text-muted">Haute</span><Input bsSize="sm" type="number" step="any" style={small} value={r.high ?? 0} onChange={(e) => { const high = num(e.target.value); set(i, { high, value: ((r.low ?? 0) + high) / 2 }); }} />
                            <span className="small text-muted">Confirmées</span><Input bsSize="sm" type="number" step="any" style={small} value={r.confirmed ?? 0} onChange={(e) => set(i, { confirmed: num(e.target.value) })} />
                        </div>
                    </td>
                </tr>
            ) : (
                <tr key={r.key}>
                    <td>{KPI_LABELS[r.key] || r.key}</td>
                    <td><Input bsSize="sm" type="number" step="any" style={small} value={r.value} onChange={(e) => set(i, { value: num(e.target.value) })} /></td>
                    <td><Input bsSize="sm" type="number" step="any" style={small} value={r.previous} onChange={(e) => set(i, { previous: num(e.target.value) })} /></td>
                    <td>{formatDelta(r.value, r.previous)}</td>
                </tr>
            ))}</tbody>
        </Table>
    );
}

const POSITION_FIELDS = [["rankClicks", "Rang clics"], ["rankSales", "Rang ventes"], ["merchants", "Distributeurs actifs"], ["clickShare", "Part des clics (0–1)"], ["conversionGap", "Écart conversion (-0,12 = -12 %)"], ["basketGap", "Écart panier"]];

function Position({ data, onChange }) {
    return (
        <Table size="sm" className="mb-0"><tbody>{POSITION_FIELDS.map(([k, label]) => (
            <tr key={k}><td>{label}</td><td><Input bsSize="sm" type="number" step="any" style={small} value={data[k] ?? 0} onChange={(e) => onChange({ ...data, [k]: num(e.target.value) })} /></td></tr>
        ))}</tbody></Table>
    );
}

function Rows({ title, rows, columns, onChange }) {
    const list = rows || [];
    const set = (i, k, v) => onChange(list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    return (
        <>
            <div className="small font-weight-bold text-muted mt-2">{title}</div>
            <Table size="sm" className="mb-0"><tbody>{list.map((r, i) => (
                <tr key={i}>
                    {columns.map(([k, type]) => (
                        <td key={k}><Input bsSize="sm" type={type} value={r[k] ?? ""} onChange={(e) => set(i, k, type === "number" ? num(e.target.value) : e.target.value)} /></td>
                    ))}
                    <td className="text-right"><Button size="sm" color="link" className="text-danger p-0" onClick={() => onChange(list.filter((_, j) => j !== i))}>Retirer</Button></td>
                </tr>
            ))}</tbody></Table>
            {list.length === 0 && <div className="small text-muted">Aucune ligne</div>}
        </>
    );
}

// Face à la concurrence : 3 équivalents par modèle, choisis parmi les candidats du robot (indices recalculés au rendu).
function Competition({ data, onChange }) {
    const models = data.models || [];
    const setShown = (mi, shown) => onChange({ ...data, models: models.map((m, j) => (j === mi ? { ...m, shown } : m)) });
    return models.map((m, mi) => {
        const shown = m.shown || [];
        const candidates = m.candidates || [];
        const label = (c) => `${c.name}${c.brand ? ` (${c.brand})` : ""}`;
        return (
            <div key={m.key} className="mb-3">
                <div className="small font-weight-bold">{m.name}</div>
                {shown.map((k, i) => (
                    <div key={i} className="d-flex align-items-center mt-1" style={{ gap: 6 }}>
                        <Input bsSize="sm" type="select" value={k} onChange={(e) => setShown(mi, shown.map((x, j) => (j === i ? e.target.value : x)))}>
                            {candidates.filter((c) => c.key === k || !shown.includes(c.key)).map((c) => <option key={c.key} value={c.key}>{label(c)}</option>)}
                        </Input>
                        <Button size="sm" color="link" className="text-danger p-0" onClick={() => setShown(mi, shown.filter((_, j) => j !== i))}>Retirer</Button>
                    </div>
                ))}
                {shown.length < 3 && candidates.some((c) => !shown.includes(c.key)) && (
                    <Button size="sm" color="link" className="p-0 mt-1" onClick={() => setShown(mi, [...shown, candidates.find((c) => !shown.includes(c.key)).key])}>+ Ajouter un équivalent</Button>
                )}
                {shown.length === 0 && <div className="small text-muted">Aucun équivalent affiché : le modèle sera omis.</div>}
            </div>
        );
    });
}

export function DataBlockEditor({ block, onChange }) {
    const data = block.data || {};
    if (block.unavailable) return <div className="small text-danger">Données indisponibles à la génération : masque ce bloc ou régénère.</div>;
    switch (block.type) {
        case "kpis":
            return <Kpis data={data} onChange={onChange} />;
        case "position":
            return <Position data={data} onChange={onChange} />;
        case "competition":
            return <Competition data={data} onChange={onChange} />;
        case "top_products":
            return (
                <>
                    <Rows title="Les plus cliqués (nom, clics)" rows={data.clicked} columns={[["name", "text"], ["clicks", "number"]]} onChange={(clicked) => onChange({ ...data, clicked })} />
                </>
            );
        case "vouchers":
            return (
                <>
                    <Rows title="Codes du partenaire (code, marchand, remise, conditions)" rows={data.own} columns={[["code", "text"], ["merchant", "text"], ["discount", "text"], ["conditions", "text"]]} onChange={(own) => onChange({ ...data, own })} />
                    <Rows title="Codes des concurrents" rows={data.competitors} columns={[["code", "text"], ["merchant", "text"], ["discount", "text"], ["conditions", "text"]]} onChange={(competitors) => onChange({ ...data, competitors })} />
                </>
            );
        default:
            return <div className="small text-muted">Bloc calculé (lecture seule) — visible dans l'aperçu.</div>;
    }
}
